import { forwardRef, memo, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import './Roulette.css'
import { pickRandom } from "mathjs";
import { animated, useSpring } from "@react-spring/web";
import { easeOutQuint } from "easing-utils";
import Pointer from "../pointers/Pointer";
import SvgBorder from "./SvgBorder";
import RouletteItemCard from "./RouletteItemCard";
import SvgWrapper from "../settings/utils/SvgWrapper";
  
const TOTAL_SLOTS = 31;
const ITEM_WIDTH = 200; // pixels
const VIEWPORT_WIDTH = 1000; // 5 items visible

const generateRoulette = (items) => {
    if (!items || items.length <= 0) return [];


    const roulette = [];
    let total = 0;

    items.forEach(item => {
        let count = Math.floor((item.probability / 100) * TOTAL_SLOTS);
        total += count;
        for (let i = 0; i < count; i++) {
            roulette.push(item);
        }
    });

    const remainingSlots = TOTAL_SLOTS - total;

    const pickedRemainingSlots = pickRandom(items, items.map(item => item.probability), remainingSlots);

    roulette.push(...pickedRemainingSlots);

    return roulette.sort(() => Math.random() - 0.5);
};

const swapElements = (array, elementIndex, newPosition) => {
    [array[elementIndex], array[newPosition]] = [array[newPosition], array[elementIndex]];
    return array;
}

const defaultBackgroundStyle = {
    radius: 16,
    color: {
        color1: "rgb(63, 63, 63)",
        color2: "rgb(63, 63, 63)",
        angle: 360
    },
    // image: {
    //     url: `${process.env.PUBLIC_URL}/images/bg_cs2_1.webp`,
    //     hueRotate: 0,
    //     saturate: 100
    // },
    shadow: {
        color: 'rgb(0, 0, 0)',
        horizontalOffset: 5,
        verticalOffset: 5,
        blur: 5
    }
}

const getBackgroundStyle = (backgroundStyle) => {

    let values = {
        borderRadius: `${backgroundStyle.radius}px`,
    };

    if (backgroundStyle.color) {
        const color = backgroundStyle.color;
        values = { ...values, ...{
            backgroundImage: `linear-gradient(${color.angle}deg, ${color.color1} 0%, ${color.color2} 100%)`
        }};
    }

    if (backgroundStyle.image) {
        const image = backgroundStyle.image;

        values = { ...values, ...{
            backgroundImage: `url("${image.url}")`,
            filter: `hue-rotate(${image.hueRotate}deg) saturate(${image.saturate}%)`,
        }};
    }

    if (backgroundStyle.shadow) {
        const shadow = backgroundStyle.shadow;
        values = { ...values, ...{
            filter: `drop-shadow(${shadow.color} ${shadow.horizontalOffset}px ${shadow.verticalOffset}px ${shadow.blur}px)`,
        }}
    }

    if (backgroundStyle.shadow && backgroundStyle.image) {
        const shadow = backgroundStyle.shadow;
        const image = backgroundStyle.image;
        values = { ...values, ...{
            filter: `drop-shadow(${shadow.color} ${shadow.horizontalOffset}px ${shadow.verticalOffset}px ${shadow.blur}px) hue-rotate(${image.hueRotate}deg) saturate(${image.saturate}%)`,
        }}
    }

    return values;
}

const defaultBorderStyle = {
    width: 6,
    radius: 16,
    color1: "rgb(165, 0, 92)",
    color2: "rgb(165, 0, 92)",
    gradientAngle: 180,
    shadow: {
        color: 'rgb(0, 0, 0)',
        horizontalOffset: 0,
        verticalOffset: 0,
        blur: 0
    }
}

const getBorderStyle = (borderStyle) => {

    let values = {}

    if (borderStyle.color) {
        const color = borderStyle.color;
        values = { ...values, ...{
            borderWidth: color.width,
            radius: color.radius,
            color1: color.color1,
            color2: color.color2,
            gradientAngle: color.gradientAngle,
        }}
    }

    if (borderStyle.image) {
        const image = borderStyle.image;
        values = { ...values, ...{
            image: image.url,
            filter: `hue-rotate(${image.hueRotate}deg) saturate(${image.saturate}%)`,
        }}
    }

    if (borderStyle.shadow) {
        const shadow = borderStyle.shadow;
        values = { ...values, ...{
            filter: `drop-shadow(${shadow.color} ${shadow.verticalOffset}px ${shadow.horizontalOffset}px ${shadow.blur}px)`
        }}
    }

    if (borderStyle.shadow && borderStyle.image) {
        const shadow = borderStyle.shadow;
        const image = borderStyle.image;
        values = { ...values, ...{
            image: image.url,
            filter: `drop-shadow(${shadow.color} ${shadow.horizontalOffset}px ${shadow.verticalOffset}px ${shadow.blur}px) hue-rotate(${image.hueRotate}deg) saturate(${image.saturate}%)`,
        }}
    }

    return values;
}

const defaultPinStyle = {
    vector: {
        component: Pointer,
        url: 'https://d3ieqpd59k74of.cloudfront.net/pointer.svg',
        stroke: 'rgb(165, 0, 92)',
        strokeWidth: 20,
        color1: 'rgb(63, 63, 63)',
        color2: 'rgb(63, 63, 63)',
        gradientAngle: 180
    },
    // image: {
    //     url: `${process.env.PUBLIC_URL}/images/Retrowave.png`,
    //     hueRotate: 0,
    //     saturate: 100
    // },
    shadow: {
        color: 'rgb(0, 0, 0)',
        horizontalOffset: 2,
        verticalOffset: 2,
        blur: 2
    }
}

const getPinStyle = (pinStyle) => {

    let values = {};

    if (pinStyle.vector) {
        const vector = pinStyle.vector;
        
        values = { ...values, ...{
            url: vector.url,
            stroke: vector.stroke, 
            strokeWidth: `${vector.strokeWidth}px`,
            color1: vector.color1,
            color2: vector.color2,
            gradientAngle: vector.gradientAngle
        }}
    }

    if (pinStyle.image) {
        const image = pinStyle.image;
        values = { ...values, ...{
            url: image.url,
            filter: `hue-rotate(${image.hueRotate}deg) saturate(${image.saturate}%)`,
        }};
    }

    if (pinStyle.shadow) {
        const shadow = pinStyle.shadow;
        values = { ...values, ...{
            filter: `drop-shadow(${shadow.color} ${shadow.horizontalOffset}px ${shadow.verticalOffset}px ${shadow.blur}px)`,
        }}
    }

    if (pinStyle.shadow && pinStyle.image) {
        const shadow = pinStyle.shadow;
        const image = pinStyle.image;
        values = { ...values, ...{
            filter: `drop-shadow(${shadow.color} ${shadow.horizontalOffset}px ${shadow.verticalOffset}px ${shadow.blur}px) hue-rotate(${image.hueRotate}deg) saturate(${image.saturate}%)`,
        }}
    }

    return values;
}

const Roulette = forwardRef(({ 
    items, 
    spinDuration = 5, 
    spinToCenter = false, 
    onCurrentItem, 
    onWin, 
    backgroundStyle = defaultBackgroundStyle,
    borderStyle = defaultBorderStyle,
    pinStyle = defaultPinStyle 
}, ref) => {

    const background = useMemo(() => getBackgroundStyle(backgroundStyle), [backgroundStyle]);
    const border = useMemo(() => getBorderStyle(borderStyle), [borderStyle]);
    const pin = useMemo(() => getPinStyle(pinStyle), [pinStyle]);

    const [{ x }, api] = useSpring(() => ({ x: 0 }));

    const [rotateAnimation, rotateAnimationApi] = useSpring(() => ({
        rotate: 0,
        config: { easing: easeOutQuint }
    }), [])

    const rouletteContainerRef = useRef();
    const itemsRef = useRef([]);
    const [rouletteItems, setRouletteItems] = useState([]);
    const cachedRouletteItems = useMemo(() => {
        if (rouletteItems.length > 0) {
            return rouletteItems;
        }
        return generateRoulette(items);
    }, [rouletteItems, items]);
    const [scale, setScale] = useState(1);
    const [currentItemIndex, setCurrentItemIndex] = useState();

    const spin = () => {
        // if (!stripRef.current) return;

        api.set({ x: 0 });

        const rouletteSlots = generateRoulette(items);

        const pickedItem = pickRandom(items, items.map(item => item.probability));
        
        const pickedElementIndex = rouletteSlots.indexOf(pickedItem);

        let swapedArray = [];

        if (pickedElementIndex !== -1) {
            swapedArray = swapElements([...rouletteSlots], pickedElementIndex, rouletteSlots.length - 5);
        } else {
            swapedArray = [...rouletteSlots];
            swapedArray[rouletteSlots.length - 5] = pickedItem;
        }

        setRouletteItems(swapedArray);

        const finalPos = spinToCenter ? 0.5 : Math.random();

        const translateShift = ((rouletteSlots.length - 5) + finalPos) * ITEM_WIDTH - VIEWPORT_WIDTH / 2;

        api.start({
            x: -translateShift,
            config: { duration: spinDuration * 1000, easing: (t) => easeOutQuint(t) },
            onChange: ({ value }) => {
                const centerElementIndex = Math.round(Math.abs(value.x / ITEM_WIDTH) + Math.floor((VIEWPORT_WIDTH / 2) / ITEM_WIDTH));

                setCurrentItemIndex(prevValue => prevValue !== centerElementIndex ? centerElementIndex : prevValue);
            },
            onRest: () => {
                console.log(`Stopped at: ${rouletteSlots.length - 5}`);
                onWin(pickedItem);
            }
        });
    }

    useEffect(() => {
        setRouletteItems([])
    }, [items])
    

    useEffect(() => {
        if (currentItemIndex >= 0) {

            const minSpeed = 20 * spinDuration / 5 > 100 ? 100 : 20 * spinDuration / 5;
            const maxSpeed = 250 * spinDuration / 5 > 500 ? 500 : 250 * spinDuration / 5;

            const rotateSpeed = minSpeed + (maxSpeed - minSpeed) / (1 + Math.exp(-6 * (currentItemIndex / TOTAL_SLOTS - 0.5)));

            rotateAnimationApi.start({
                from: { rotate: 0 },
                to: [
                    { rotate: 25 }, // Kick up
                    { rotate: 0 }   // Return down
                ],
                config: { 
                    duration: rotateSpeed, 
                    easing: easeOutQuint 
                },
                reset: true // Important: Forces the animation to restart even if it thinks it's done
            });

            if (onCurrentItem) {
                onCurrentItem({ index: currentItemIndex, value: cachedRouletteItems[currentItemIndex] })
            }
        }
    }, [cachedRouletteItems, onCurrentItem, currentItemIndex])

    useEffect(() => {
        const observer = new ResizeObserver(() => {

            if (!rouletteContainerRef.current) {
                return;
            }
            const rect = rouletteContainerRef.current.getBoundingClientRect();

            const baseWidth = 1000;
            const newScale = Math.min(rect.width / baseWidth, 1);
            setScale(newScale);
        });

        if (rouletteContainerRef.current) {
            observer.observe(rouletteContainerRef.current);
        }

        return () => observer.disconnect();
    }, [])

    useImperativeHandle(ref, () => ({
        spin
    }), [spin]);

    return (
        <div ref={rouletteContainerRef}>
            <div className="roulette-container">
                <div
                    className="roulette-scale-wrapper"
                    style={{
                        transform: `scale(${scale})`,
                        transformOrigin: "center center"
                    }}
                >
                    {borderStyle.color && Object.keys(borderStyle).length > 0 && <SvgBorder 
                        id="roulette-border"
                        width={'1010'} 
                        height={'210'} 
                        borderWidth={border.borderWidth} 
                        radius={border.radius} 
                        gradient={[
                            { offset: "0%", color: border.color1 },
                            { offset: "100%", color: border.color2 }
                        ]}
                        gradientAngle={border.gradientAngle}
                        style={{ position: 'absolute', zIndex: 1, filter: border.filter }}
                    />}
                    {borderStyle.image && <div className="roulette-frame" style={border}>
                        <img src={border.image}></img>
                    </div>}
                    <div className="roulette-background" style={background}></div>
                    <animated.div className="roulette-arrow" style={{ transform: 'translateX(-50%)', ...rotateAnimation }}>
                        <div className="roulette-arrow-pic">
                            { pinStyle.vector && pin.url && <SvgWrapper id="_555" className="roulette-arrow-vector" src={pin.url} style={pin} gradient={[ { offset: "0%", color: pin.color1 }, { offset: "100%", color: pin.color2 } ]} gradientAngle={pin.gradientAngle} />}
                            { pinStyle.image && <img className="roulette-arrow-image" src={pin.url} style={pin} /> }
                        </div>
                    </animated.div>
                    <div className="roulette-viewport">
                        <animated.div
                            className="roulette-strip"
                            style={{
                                display: "flex",
                                transform: x.to((x) => `translateX(${x}px)`),
                            }}
                        >
                            {cachedRouletteItems.map((item, index) => <RouletteItem key={index} ref={(el) => (itemsRef.current[index] = el)} index={index} item={item} />)}
                        </animated.div>
                    </div>
                </div>
            </div>
        </div>
    )
})

const RouletteItem = memo(forwardRef(({ index, item }, ref) => {

    return (
        <div ref={ref} className="roulette-item-wrapper">
            <RouletteItemCard
                index={index}
                item={item}
                cardStyle={item?.cardStyle}
                borderStyle={item?.borderStyle}
                textStyle={item?.textStyle} 
            />
        </div>
    )
}))

export default memo(Roulette);